import axios from 'axios'
import { cacheAdapterEnhancer } from 'axios-extensions'
import store from '../../store'

// Variables to manage token refresh state
let isRefreshing = false
let failedQueue = []

// Configuration for caching
const cacheConfig = {
  enabledByDefault: false,
  cacheFlag: 'useCache'
}

// Create a custom Axios instance with cache adapter
const customAxios = axios.create({
  headers: {
    'Cache-Control': 'no-cache'
  },
  adapter: cacheAdapterEnhancer(axios.defaults.adapter, cacheConfig),
})

// Function to process failed requests queue after refreshing the token
const processQueue = (error, token = null) => {
  console.log('processQueue ->', failedQueue)
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error)
    } else {
      prom.resolve(token)
    }
  })
  failedQueue = []
}

// Interceptor to attach access token to each outgoing request
customAxios.interceptors.request.use(
  config => {
    const accessToken = localStorage.getItem('access_token')

    if (accessToken) {
      config.headers['Authorization'] = `Bearer ${accessToken}`
    }
    return config
  },
  error => Promise.reject(error)
)

// Interceptor to handle responses, particularly 401 Unauthorized errors
customAxios.interceptors.response.use(
  response => response, // Just return the response if it's successful
  async error => {
    const originalRequest = error.config

    // Check for 401 Unauthorized
    if (error.response.status === 401 && !originalRequest._retry) {
      console.log('UNAUTHORISED 401 ->', originalRequest.url)
      console.log('isRefreshing ->', isRefreshing)
      
      if (isRefreshing) {
        // If a token refresh is already in progress, queue the failed request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject })
        })
        .then(token => {
          // Set the new token in the request header and retry the original request
          originalRequest.headers['Authorization'] = `Bearer ${token}`
          return customAxios(originalRequest)
        })
        .catch(err => Promise.reject(err))
      }

      originalRequest._retry = true // Mark the original request as retried
      isRefreshing = true // Set the refreshing state to true

      try {
        // Call the refreshToken action to get a new access token
        const newAccessToken = await store.dispatch('auth/refreshToken')
        
        // Update default authorization header
        customAxios.defaults.headers['Authorization'] = `Bearer ${newAccessToken}`
        
        // Update the original request's authorization header
        originalRequest.headers['Authorization'] = `Bearer ${newAccessToken}`

        // Process the queue with the new token
        processQueue(null, newAccessToken)
        
        // Retry the original request with the new token
        return customAxios(originalRequest)
      } catch (err) {
        // Process the queue with the error
        processQueue(err, null)
        return Promise.reject(err)
      } finally {
        isRefreshing = false
      }
      
    } else if (error.response.status === 403) {
      console.log('403 FORBIDDEN - this call wont be retried. Should this be a 401?')
    }

    return Promise.reject(error)
  }
)

export default customAxios
